import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import { useTheme } from "../ThemeProvider";
import { useLanguage } from "../LanguageProvider";
import { cn } from "../../lib/utils";

export const TopHeader = ({ onMobileMenuToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");

  const pageTitles = {
    "/": t("dashboard"),
    "/ontology": t("ontologyBuilder"),
    "/graph": t("graphExplorer"),
    "/map": t("gisMapView"),
    "/timeline": t("timelineAnalytics"),
    "/search": t("searchResults"),
    "/worksheet": t("worksheetBuilder"),
    "/alerts": t("alertsReports"),
    "/admin": t("adminSettings"),
    "/design-system": t("designSystem"),
  };

  const pageTitle = pageTitles[location.pathname] || "DataForge";

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 flex h-14 lg:h-16 items-center justify-between border-b border-border bg-white dark:bg-card px-3 lg:px-6 z-50 lg:sticky lg:left-auto lg:right-auto">
      {/* Left: Mobile Menu + Page Title */}
      <div className="flex items-center gap-2 lg:gap-4">
        {/* Mobile Menu Button */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-9 w-9 lg:hidden"
          onClick={onMobileMenuToggle}
          data-testid="mobile-menu-toggle"
        >
          <i className="fa-solid fa-bars text-base"></i>
        </Button>
        
        <h1 className="text-sm lg:text-lg font-semibold text-foreground truncate max-w-[140px] sm:max-w-none">{pageTitle}</h1>
        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 hidden sm:flex">
          <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-success animate-pulse"></span>
          {t("live")}
        </Badge>
      </div>

      {/* Center: Global Search - Hidden on mobile */}
      <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-2 lg:mx-8 hidden md:block">
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
          <Input
            type="search"
            placeholder={t("search") + "..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-20 bg-muted/50 border-transparent focus:border-primary focus:bg-background h-9"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </div>
      </form>

      {/* Right: Actions */}
      <div className="flex items-center gap-1 lg:gap-2">
        {/* Language Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 lg:h-9 lg:w-9" data-testid="language-toggle">
              <span className="text-xs lg:text-sm font-semibold">{language.toUpperCase()}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setLanguage("en")} className={language === "en" ? "bg-accent" : ""}>
              <span className="mr-2">🇬🇧</span>
              {t("english")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLanguage("de")} className={language === "de" ? "bg-accent" : ""}>
              <span className="mr-2">🇩🇪</span>
              {t("german")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 lg:h-9 lg:w-9">
              <i className={cn(
                "text-sm lg:text-base",
                theme === "dark" ? "fa-solid fa-moon" : "fa-solid fa-sun"
              )}></i>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")} className="flex items-center">
              <i className="fa-solid fa-sun w-4 text-center mr-2"></i>
              {t("light")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")} className="flex items-center">
              <i className="fa-solid fa-moon w-4 text-center mr-2"></i>
              {t("dark")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")} className="flex items-center">
              <i className="fa-solid fa-desktop w-4 text-center mr-2"></i>
              System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 lg:h-9 lg:w-9 relative">
              <i className="fa-solid fa-bell text-sm lg:text-base"></i>
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                3
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 lg:w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              {t("notifications")}
              <Badge variant="secondary" className="text-xs">3 {t("new")}</Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-y-auto">
              <DropdownMenuItem className="flex flex-col items-start gap-1 p-3">
                <div className="flex items-center gap-2 w-full">
                  <div className="h-2 w-2 rounded-full bg-destructive"></div>
                  <span className="font-medium text-sm">{t("criticalAlert")}</span>
                  <span className="text-xs text-muted-foreground ml-auto">2m</span>
                </div>
                <p className="text-xs text-muted-foreground pl-4">
                  {t("anomalyDetected")}
                </p>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex flex-col items-start gap-1 p-3">
                <div className="flex items-center gap-2 w-full">
                  <div className="h-2 w-2 rounded-full bg-warning"></div>
                  <span className="font-medium text-sm">{t("dataImportComplete")}</span>
                  <span className="text-xs text-muted-foreground ml-auto">15m</span>
                </div>
                <p className="text-xs text-muted-foreground pl-4">
                  {t("recordsProcessed")}
                </p>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex flex-col items-start gap-1 p-3">
                <div className="flex items-center gap-2 w-full">
                  <div className="h-2 w-2 rounded-full bg-primary"></div>
                  <span className="font-medium text-sm">{t("reportGenerated")}</span>
                  <span className="text-xs text-muted-foreground ml-auto">1h</span>
                </div>
                <p className="text-xs text-muted-foreground pl-4">
                  {t("weeklyReportReady")}
                </p>
              </DropdownMenuItem>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="justify-center text-primary font-medium">
              {t("viewAllNotifications")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Help - Hidden on mobile */}
        <Button variant="ghost" size="icon" className="h-8 w-8 lg:h-9 lg:w-9 hidden sm:flex">
          <i className="fa-solid fa-circle-question text-sm lg:text-base"></i>
        </Button>

        {/* Quick Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 h-8 lg:h-9 px-2 lg:px-3">
              <i className="fa-solid fa-plus text-xs lg:text-sm"></i>
              <span className="hidden sm:inline">{t("new")}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate("/ontology")}>
              <i className="fa-solid fa-diagram-project mr-2"></i>
              {t("newOntology")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/worksheet")}>
              <i className="fa-solid fa-table-cells-large mr-2"></i>
              {t("newWorksheet")}
            </DropdownMenuItem>
            <DropdownMenuItem>
              <i className="fa-solid fa-file-import mr-2"></i>
              {t("importData")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <i className="fa-solid fa-folder-plus mr-2"></i>
              {t("newProject")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
